// src/pages/AdminTeam.jsx
// STATUS: active | UPDATED: 2026-09-18
// LOG:
//   2026-09-18  Agents excluded. The header counts this list and says people.
//   2026-08-30  New. Everybody in the system, grouped by the store they work at.
//
// The Level 00 roster. /team/ is a manager looking at one floor. This is Jazz
// looking at all of them at once.
//
// GROUPED BY BUSINESS, BECAUSE THAT IS HOW HE THINKS ABOUT THEM
//
// A flat list of every person across every dealership is a spreadsheet. Jazz
// does not know a salesperson, he knows a store, so the store is the unit and
// the people are inside it. Closed by default: the count is usually the answer,
// and opening one is a deliberate act.
//
// JAZZ'S TEAM IS THE PEOPLE WITH NO STORE
//
// A platform admin has `dealership_id = null`, which is exactly what makes them
// Level 00. That is not a missing value to be tidied up, it is the definition,
// so they group together under Jazz's Team and sit first.

import { useCallback, useEffect, useState } from 'react'
import {
  Box, Flex, HStack, VStack, Text, Icon, Badge, Spinner, Alert, AlertIcon, Collapse,
} from '@chakra-ui/react'
import { Users, ChevronRight, Shield, Building2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { displayNameFor } from '../lib/names.js'
import { roleLabel } from '../lib/roles'

const ROLE_ORDER = { admin: 0, owner: 1, manager: 2, employee: 3 }

function Member({ m }) {
  const name = displayNameFor(m, { email: m.email })
  return (
    <Flex align="center" gap={3} py={2.5} borderTop="1px solid" borderColor="line">
      <Box
        w="30px" h="30px" borderRadius="full" bg="surface" flexShrink={0}
        display="flex" alignItems="center" justifyContent="center"
        fontSize="11px" fontWeight={700} color="inkMuted"
      >
        {(name || '?').slice(0, 2).toUpperCase()}
      </Box>
      <Box flex={1} minW={0}>
        <Text fontSize="body-sm" fontWeight={600} color="ink" noOfLines={1}>
          {name}
        </Text>
        <Text fontSize="12px" color="inkMuted" noOfLines={1}>
          {m.email}
        </Text>
      </Box>
      <Badge
        bg="surface" color="inkMuted" fontSize="10px" fontWeight={600}
        textTransform="uppercase" letterSpacing="0.07em" px={2} py={0.5} borderRadius="full"
      >
        {roleLabel(m.role) || 'no role'}
      </Badge>
    </Flex>
  )
}

function Group({ group }) {
  const [open, setOpen] = useState(false)
  const counts = group.members.reduce((a, m) => {
    a[m.role] = (a[m.role] || 0) + 1
    return a
  }, {})
  const summary = Object.entries(counts)
    .sort((a, b) => (ROLE_ORDER[a[0]] ?? 9) - (ROLE_ORDER[b[0]] ?? 9))
    .map(([r, n]) => `${n} ${roleLabel(r)?.toLowerCase() || r}${n > 1 ? 's' : ''}`)
    .join(', ')

  return (
    <Box
      bg="card"
      border="1px solid"
      borderColor="line"
      borderLeftWidth={group.internal ? '3px' : '1px'}
      borderLeftColor={group.internal ? 'accent.500' : 'line'}
      borderRadius="card"
      overflow="hidden"
    >
      <Flex
        as="button"
        w="100%"
        textAlign="left"
        align="center"
        gap={3}
        px={{ base: 5, md: 6 }}
        py={4}
        onClick={() => setOpen((v) => !v)}
        _hover={{ bg: 'surface' }}
        transition="background 0.12s ease"
      >
        <Icon
          as={group.internal ? Shield : Building2}
          boxSize={4}
          color={group.internal ? 'accent.500' : 'inkMuted'}
          flexShrink={0}
        />
        <Box flex={1} minW={0}>
          <Text fontSize="body" fontWeight={600} color="ink" noOfLines={1}>
            {group.name}
          </Text>
          <Text fontSize="12px" color="inkMuted" noOfLines={1}>
            {summary || 'nobody yet'}
            {group.locations ? ` · ${group.locations} location${group.locations > 1 ? 's' : ''}` : ''}
          </Text>
        </Box>
        <Text fontSize="xl" fontWeight={700} color="ink" fontVariantNumeric="tabular-nums">
          {group.members.length}
        </Text>
        <Icon
          as={ChevronRight}
          boxSize={4}
          color="inkMuted"
          transform={open ? 'rotate(90deg)' : 'none'}
          transition="transform 0.15s ease"
          flexShrink={0}
        />
      </Flex>

      <Collapse in={open} animateOpacity>
        <Box px={{ base: 5, md: 6 }} pb={4}>
          {group.members
            .slice()
            .sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9))
            .map((m) => <Member key={m.id} m={m} />)}
        </Box>
      </Collapse>
    </Box>
  )
}

export default function AdminTeam() {
  const [state, setState] = useState({ loading: true, groups: [], error: null })

  const load = useCallback(async () => {
    const [usersRes, storesRes] = await Promise.all([
      // Agents are excluded, not hidden. This page manages people: their
      // role, their store, their invite. Otto has none of those and the
      // header counts what is in this list and calls them people.
      supabase.from('users').select('id, email, display_name, initials, role, dealership_id').eq('is_agent', false),
      supabase.from('dealerships').select('id, name, locations(id)'),
    ])

    if (usersRes.error || storesRes.error) {
      setState({ loading: false, groups: [], error: 'Could not load the roster.' })
      return
    }

    const stores = storesRes.data || []
    const users = usersRes.data || []

    // A platform admin has no dealership. That is the definition, not a gap.
    const internal = users.filter((u) => !u.dealership_id)
    const groups = [
      { id: 'internal', name: "Jazz's Team", internal: true, locations: 0, members: internal },
      ...stores
        .map((s) => ({
          id: s.id,
          name: s.name,
          internal: false,
          locations: (s.locations || []).length,
          members: users.filter((u) => u.dealership_id === s.id),
        }))
        .sort((a, b) => b.members.length - a.members.length || a.name.localeCompare(b.name)),
    ]

    setState({ loading: false, groups, error: null })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const total = state.groups.reduce((a, g) => a + g.members.length, 0)

  return (
    <VStack align="stretch" spacing={6} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          Operator
        </Text>
        <Text textStyle="pageTitle">Everybody</Text>
        <Text fontSize="body" color="inkMuted" lineHeight={1.65} maxW="measure" mt={3}>
          Every person in the system, grouped by the business they work for. Open one
          to see who is in it.
        </Text>
      </Box>

      {state.loading && (
        <Flex justify="center" py={10}>
          <Spinner size="md" color="ink" thickness="3px" />
        </Flex>
      )}

      {state.error && (
        <Alert status="error" borderRadius="md" fontSize="sm">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      {!state.loading && !state.error && (
        <>
          <HStack spacing={2}>
            <Icon as={Users} boxSize={4} color="inkMuted" />
            <Text textStyle="eyebrow" color="inkMuted">
              {total} {total === 1 ? 'person' : 'people'} across {state.groups.length} groups
            </Text>
          </HStack>

          <VStack align="stretch" spacing={3}>
            {state.groups.map((g) => <Group key={g.id} group={g} />)}
          </VStack>
        </>
      )}
    </VStack>
  )
}
